// Pure logic for the kitchen alerts setting (Stage 3). Mirrors the backend's
// kitchen_flow.alerts shape and its phone normalisation.
export const ALERT_RULES = ['req_short', 'transit_variance', 'avt', 'price_jump', 'expiring', 'po_overdue', 'equipment_out'] as const
export type AlertRule = (typeof ALERT_RULES)[number]

export interface AlertRecipient {
  label: string
  phone: string
}

export interface KitchenAlertsConfig {
  enabled: boolean
  recipients: AlertRecipient[]
  rules: Record<string, boolean>
  avt_pct: number
  price_jump_pct: number
  expiry_days: number
  overdue_po_days: number
  digest: boolean
  digest_hour: number
}

export const ALERT_DEFAULTS: KitchenAlertsConfig = {
  enabled: false, recipients: [], rules: {}, avt_pct: 5, price_jump_pct: 10,
  expiry_days: 3, overdue_po_days: 0, digest: true, digest_hour: 8,
}

const num = (v: unknown, d: number) => (typeof v === 'number' && Number.isFinite(v) ? v : d)

/** Reads the stored setting (unknown shape) with defaults; off unless switched on. */
export function parseAlertsConfig(raw: unknown): KitchenAlertsConfig {
  const o = raw && typeof raw === 'object' && !Array.isArray(raw) ? (raw as Record<string, unknown>) : {}
  const recipients = Array.isArray(o.recipients)
    ? (o.recipients as unknown[]).flatMap((r) => {
      const x = r as Record<string, unknown>
      return x && typeof x.phone === 'string' ? [{ label: typeof x.label === 'string' ? x.label : '', phone: x.phone }] : []
    })
    : []
  return {
    enabled: o.enabled === true,
    recipients,
    rules: o.rules && typeof o.rules === 'object' ? { ...(o.rules as Record<string, boolean>) } : {},
    avt_pct: num(o.avt_pct, ALERT_DEFAULTS.avt_pct) || ALERT_DEFAULTS.avt_pct,
    price_jump_pct: num(o.price_jump_pct, ALERT_DEFAULTS.price_jump_pct) || ALERT_DEFAULTS.price_jump_pct,
    // 0 is a real value here (expiring today / a midnight digest): only a
    // missing number falls back to the default.
    expiry_days: num(o.expiry_days, ALERT_DEFAULTS.expiry_days),
    overdue_po_days: num(o.overdue_po_days, ALERT_DEFAULTS.overdue_po_days),
    digest: o.digest !== false,
    digest_hour: num(o.digest_hour, ALERT_DEFAULTS.digest_hour),
  }
}

export function ruleOn(c: KitchenAlertsConfig, rule: string): boolean {
  return c.rules[rule] !== false
}

/** Same rules as normaliseStaffPhone in Go: +92… / 92… / 03… / 3xx…, else E.164 with +. */
export function normalisePhone(p: string): string | null {
  const digits = p.replace(/\D/g, '')
  if (digits.length === 12 && digits.startsWith('92')) return `+${digits}`
  if (digits.length === 11 && digits.startsWith('0')) return `+92${digits.slice(1)}`
  if (digits.length === 10 && digits.startsWith('3')) return `+92${digits}`
  if (p.trim().startsWith('+') && digits.length >= 8 && digits.length <= 15) return `+${digits}`
  return null
}

export type AlertNumberField = 'avt_pct' | 'price_jump_pct' | 'expiry_days' | 'overdue_po_days' | 'digest_hour'

/**
 * The server's limits (validateKitchenAlertsSetting in Go): percentages
 * 0–100 (usage) and 0–1000 (price), whole days 0–60, a whole hour 0–23.
 */
export const ALERT_LIMITS: Record<AlertNumberField, { min: number; max: number; whole: boolean }> = {
  avt_pct: { min: 0, max: 100, whole: false },
  price_jump_pct: { min: 0, max: 1000, whole: false },
  expiry_days: { min: 0, max: 60, whole: true },
  overdue_po_days: { min: 0, max: 60, whole: true },
  digest_hour: { min: 0, max: 23, whole: true },
}

/** Each number field that the server would refuse, with why. */
export function alertsFieldErrors(c: KitchenAlertsConfig): Partial<Record<AlertNumberField, 'missing' | 'range' | 'whole'>> {
  const out: Partial<Record<AlertNumberField, 'missing' | 'range' | 'whole'>> = {}
  for (const key of Object.keys(ALERT_LIMITS) as AlertNumberField[]) {
    if (key === 'digest_hour' && !c.digest) continue
    const v = c[key]
    const lim = ALERT_LIMITS[key]
    if (!Number.isFinite(v)) out[key] = 'missing'
    else if (v < lim.min || v > lim.max) out[key] = 'range'
    else if (lim.whole && !Number.isInteger(v)) out[key] = 'whole'
  }
  return out
}

/** Why the config cannot be saved yet, or null. */
export function alertsConfigError(c: KitchenAlertsConfig): string | null {
  if (c.enabled && c.recipients.length === 0) return 'need_number'
  if (c.recipients.length > 10) return 'too_many'
  if (c.recipients.some((r) => normalisePhone(r.phone) == null)) return 'bad_phone'
  if (Object.keys(alertsFieldErrors(c)).length > 0) return 'bad_number'
  return null
}

/** Fills a template preview's {{n}} slots — how the WhatsApp message will read. */
export function renderTemplatePreview(preview: string, params: string[]): string {
  return preview.replace(/\{\{(\d+)\}\}/g, (_, n: string) => params[Number(n) - 1] ?? `{{${n}}}`)
}

/** The approved-template bodies (must match messaging/wa_templates.go). */
export const KITCHEN_TEMPLATES = {
  alert: 'Kitchen alert for {{1}}: {{2}}. {{3}}. Open Kitchen Flow in Bhookly for the details.',
  digest: 'Good morning. Kitchen summary for {{1}}, {{2}}. Store requests: {{3}}. Missing: {{4}}. Stock: {{5}}. Buying: {{6}}. Open Kitchen Flow in Bhookly for the details.',
}
